import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronLeft, CheckCircle2 } from 'lucide-react';
type Answers = {
  joint: string;
  diagnosis: string;
  xray: string;
  travel: string;
};
const QUESTIONS = [{
  key: 'joint' as const,
  title: 'Какой сустав вас беспокоит?',
  options: ['Коленный', 'Тазобедренный']
}, {
  key: 'diagnosis' as const,
  title: 'Поставлен ли вам диагноз — гонартроз или коксартроз?',
  options: ['Да, диагноз подтверждён', 'Не уверен(а), нужна консультация']
}, {
  key: 'xray' as const,
  title: 'Есть ли у вас на руках рентген?',
  options: ['Да, есть', 'Пока нет, но готов(а) сделать']
}, {
  key: 'travel' as const,
  title: 'Готовы ли вы приехать на операцию в Санкт-Петербург?',
  options: ['Да, готов(а)', 'Пока не готов(а)']
}];
const TOTAL_STEPS = QUESTIONS.length + 1; // 4 questions + contact step
const transition = {
  duration: 0.28,
  ease: [0.16, 1, 0.3, 1] as const
};
export function LeadFormStepper() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({
    joint: '',
    diagnosis: '',
    xray: '',
    travel: ''
  });
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const isContactStep = step === QUESTIONS.length;
  function selectOption(key: keyof Answers, value: string) {
    setAnswers(prev => ({
      ...prev,
      [key]: value
    }));
    setStep(s => s + 1);
  }
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) return;
    setSubmitted(true);
  }
  if (submitted) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.96
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={transition} className="rounded-3xl border border-blue-100 bg-white p-8 text-center shadow-sm sm:p-12" role="status" aria-live="polite" data-id="element-51">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-green-100" data-id="element-52">
          <CheckCircle2 className="h-9 w-9 text-green-600" aria-hidden="true" data-id="element-53" />
        </div>
        <h3 className="text-2xl font-semibold text-slate-900" data-id="element-54">
          Заявка отправлена
        </h3>
        <p className="mt-3 text-lg text-slate-600" data-id="element-55">
          Спасибо, {name.trim() || 'друг'}! Мы свяжемся с вами в течение 24
          часов.
        </p>
      </motion.div>;
  }
  return <div className="rounded-3xl border border-blue-100 bg-white p-6 shadow-sm sm:p-8" data-id="element-56">
      {/* Progress indicator */}
      <div className="mb-8" data-id="element-57">
        <div className="mb-3 flex items-center justify-between text-base font-medium text-slate-500" data-id="element-58">
          <span data-id="element-59">
            Шаг {step + 1} из {TOTAL_STEPS}
          </span>
          <span data-id="element-60">{Math.round((step + 1) / TOTAL_STEPS * 100)}%</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100" data-id="element-61">
          <motion.div className="h-full rounded-full bg-blue-600" initial={false} animate={{
          width: `${(step + 1) / TOTAL_STEPS * 100}%`
        }} transition={transition} data-id="element-62" />
        </div>
      </div>

      {/* Back button */}
      {step > 0 && <button type="button" onClick={() => setStep(s => Math.max(0, s - 1))} className="mb-5 inline-flex items-center gap-1 rounded-lg px-2 py-1 text-base font-medium text-slate-500 transition hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-63">
          <ChevronLeft className="h-5 w-5" aria-hidden="true" data-id="element-64" />
          Назад
        </button>}

      <AnimatePresence mode="wait" data-id="element-65">
        {!isContactStep ? <motion.div key={`q-${step}`} initial={{
        opacity: 0,
        x: 24
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -24
      }} transition={transition} data-id="element-66">
            <h3 className="text-xl font-semibold text-slate-900 sm:text-2xl" data-id="element-67">
              {QUESTIONS[step].title}
            </h3>
            <div className="mt-6 grid gap-4 sm:grid-cols-2" data-id="element-68">
              {QUESTIONS[step].options.map(option => {
            const key = QUESTIONS[step].key;
            const selected = answers[key] === option;
            return <button key={option} type="button" onClick={() => selectOption(key, option)} aria-pressed={selected} className={`flex items-center justify-between gap-3 rounded-2xl border-2 p-5 text-left text-lg font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${selected ? 'border-blue-600 bg-blue-50 text-slate-900' : 'border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50/40'}`} data-id="element-69">
                    <span data-id="element-70">{option}</span>
                    <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 ${selected ? 'border-blue-600 bg-blue-600' : 'border-slate-300'}`} data-id="element-71">
                      {selected && <Check className="h-4 w-4 text-white" aria-hidden="true" data-id="element-72" />}
                    </span>
                  </button>;
          })}
            </div>
          </motion.div> : <motion.form key="contact" initial={{
        opacity: 0,
        x: 24
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -24
      }} transition={transition} onSubmit={handleSubmit} className="space-y-5" data-id="element-73">
            <h3 className="text-xl font-semibold text-slate-900 sm:text-2xl" data-id="element-74">
              Оставьте контакты для связи
            </h3>
            <div data-id="element-75">
              <label htmlFor="lead-name" className="mb-2 block text-base font-medium text-slate-700" data-id="element-76">
                Ваше имя
              </label>
              <input id="lead-name" type="text" value={name} onChange={e => setName(e.target.value)} required autoComplete="name" placeholder="Например, Анна" className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-lg text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30" data-id="element-77" />
            </div>
            <div data-id="element-78">
              <label htmlFor="lead-phone" className="mb-2 block text-base font-medium text-slate-700" data-id="element-79">
                Телефон для связи
              </label>
              <input id="lead-phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} required autoComplete="tel" placeholder="+7 (___) ___-__-__" className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-lg text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30" data-id="element-80" />
            </div>
            <button type="submit" className="w-full rounded-xl bg-blue-600 px-6 py-4 text-lg font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2" data-id="element-81">
              Отправить заявку
            </button>
            <p className="text-center text-base text-slate-500" data-id="element-82">
              Мы свяжемся с вами в течение 24 часов
            </p>
          </motion.form>}
      </AnimatePresence>
    </div>;
}